'use client';

import { useEffect } from 'react';
import Image from 'next/image';
import { AccessiWayConfig } from '@/types';

type AccessiWayWidgetProps = {
  size?: 'small' | 'large';
};

const ACCESSIWAY_SCRIPT_ID = 'accessiway-widget-script';
const ACCESSIWAY_SCRIPT_SRC = 'https://embeds.acswapp.com/accessibility/app.js';

const ACCESSIWAY_CONFIG: AccessiWayConfig = {
  statementLink: '',
  footerHtml: '<a href="https://www.accessiway.com/it/home/" target="_blank">AccessiWay</a>',
  hideMobile: false,
  hideTrigger: true, // Nascondi il trigger di default
  language: 'it',
  position: 'left',
  leadColor: '#0069fb',
  triggerColor: '#004fce',
  triggerRadius: '50%',
  triggerPositionX: 'left',
  triggerPositionY: 'bottom',
  triggerIcon: 'people',
  triggerSize: 'medium',
  triggerOffsetX: 20,
  triggerOffsetY: 20,
  mobile: {
    triggerSize: 'medium',
    triggerPositionX: 'left',
    triggerPositionY: 'bottom',
    triggerOffsetX: 10,
    triggerOffsetY: 10,
    triggerRadius: '50%',
  },
};

// Il loader ufficiale chiama `acsbJS.init()` senza config: intercettiamo l'assegnazione di `window.acsbJS`
// fatta da app.js per passare la nostra config quando init viene chiamato senza argomenti.
const injectConfigOnInit = () => {
  let api: Window['acsbJS'] | undefined = window.acsbJS;

  Object.defineProperty(window, 'acsbJS', {
    configurable: true,
    enumerable: true,
    get: () => api,
    set: (value: Window['acsbJS'] | undefined) => {
      if (value && value !== api && typeof value.init === 'function') {
        const originalInit = value.init;
        value.init = config => originalInit.call(value, config ?? ACCESSIWAY_CONFIG);
      }
      api = value;
    },
  });
};

const getImageSize = (variant: AccessiWayWidgetProps['size']) => {
  switch (variant) {
    case 'small':
      return { width: 26, height: 26, className: 'h-7 w-7' };
    case 'large':
    default:
      return { width: 48, height: 48, className: 'h-12 w-12' };
  }
};

export const AccessiWayWidget = ({ size = 'large' }: AccessiWayWidgetProps) => {
  useEffect(() => {
    // Evita duplicati
    if (window.acsbJS?.initialized || document.getElementById(ACCESSIWAY_SCRIPT_ID)) return;

    injectConfigOnInit();

    const s = document.createElement('script');
    s.id = ACCESSIWAY_SCRIPT_ID;
    s.src = ACCESSIWAY_SCRIPT_SRC;
    s.async = true;

    const target = !document.body ? document.querySelector('head') : document.body;
    target?.appendChild(s);
  }, []);

  return (
    <button
      data-acsb="trigger"
      data-acsb-custom-trigger="true"
      className="cursor-pointer transition-all hover:scale-110"
      aria-label="Opzioni di accessibilità"
      title="Apri opzioni di accessibilità"
    >
      <Image src="/accessiWayWidget.svg" alt="Accessibilità" unoptimized {...getImageSize(size)} />
    </button>
  );
};
